"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import {
    Loader2,
    AlertCircle,
    CheckCircle2,
    ShoppingBag,
    X,
    Shield,
    Zap,
    Coins,
    ArrowRight,
    User,
    Server,
    Package,
    Sparkles
} from "lucide-react"
import { useSession } from "next-auth/react"

interface ShopPack {
    spack_id: number
    nome: string
    valor: number
    imagem: string | null
    server_id: string | null
    items: Array<{
        sitem_id: number
        nome: string
        amount: number
        enchant: number
    }>
}

interface Character {
    charId: number
    char_name: string
    level: number
    classid: number
    online: number
    account_name: string
    server_id: string
}

interface ShopPurchaseModalProps {
    pack: ShopPack | null
    isOpen: boolean
    onClose: () => void
    onPurchaseSuccess: (newBalance: number) => void
}

export function ShopPurchaseModal({ pack, isOpen, onClose, onPurchaseSuccess }: ShopPurchaseModalProps) {
    const { data: session } = useSession()
    const [step, setStep] = useState<"select" | "confirm" | "processing" | "success">("select")
    const [characters, setCharacters] = useState<Character[]>([])
    const [selectedChar, setSelectedChar] = useState<string>("")
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        if (isOpen && session) {
            fetchCharacters()
            setStep("select")
            setError("")
            setSelectedChar("")
        }
    }, [isOpen, session, pack])

    const fetchCharacters = async () => {
        setLoading(true)
        try {
            const res = await fetch("/api/dashboard/characters")
            if (res.ok) {
                const data = await res.json()
                let allChars: Character[] = []
                data.accounts?.forEach((acc: any) => {
                    acc.characters?.forEach((char: any) => {
                        allChars.push({
                            ...char,
                            account_name: acc.login,
                            server_id: acc.server_id
                        })
                    })
                })

                if (pack?.server_id) {
                    allChars = allChars.filter(c => c.server_id === pack.server_id)
                }

                setCharacters(allChars)
            }
        } catch (err) {
            console.error(err)
            setError("Failed to load characters")
        } finally {
            setLoading(false)
        }
    }

    const handlePurchase = async () => {
        if (!pack || !selectedChar) return

        setStep("processing")
        setError("")

        try {
            const char = characters.find(c => c.char_name === selectedChar)
            if (!char) throw new Error("Character not found")

            const res = await fetch("/api/shop/purchase", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    packId: pack.spack_id,
                    characterName: char.char_name,
                    gameAccount: char.account_name
                })
            })

            const data = await res.json()

            if (!res.ok) {
                throw new Error(data.error || "Purchase failed")
            }

            setStep("success")
            onPurchaseSuccess(data.newBalance)
        } catch (err: any) {
            setStep("confirm")
            setError(err.message)
        }
    }

    const getSelectedCharDetails = () => characters.find(c => c.char_name === selectedChar)

    if (!isOpen || !pack) return null

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="relative w-full max-w-lg rounded-3xl bg-slate-900/40 border border-white/10 shadow-[0_0_50px_-12px_rgba(168,85,247,0.4)] overflow-hidden flex flex-col animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">

                {/* Background Accents */}
                <div className="absolute top-0 left-0 w-full h-[150px] bg-gradient-to-b from-purple-600/20 to-transparent pointer-events-none" />
                <div className="absolute top-[-50px] right-[-50px] w-[200px] h-[200px] bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />

                {/* Header */}
                <div className="relative z-10 flex items-start justify-between p-8 pb-4">
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            <div className="absolute inset-0 bg-purple-500 blur-xl opacity-20 animate-pulse" />
                            <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg border border-white/20">
                                <ShoppingBag className="w-6 h-6 text-white" />
                            </div>
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-white tracking-tight uppercase italic leading-none">
                                {step === "success" ? "Order Complete" : "Checkout"}
                            </h2>
                            <p className="text-purple-400 font-bold text-xs uppercase tracking-[0.2em] mt-1.5 flex items-center gap-2">
                                <Sparkles className="w-3 h-3" />
                                {pack.nome}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/5"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Main Content Area */}
                <div className="relative z-10 flex-grow px-8 pb-8">

                    {/* Error Banner */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-400 animate-in slide-in-from-top-2 duration-300">
                            <AlertCircle className="w-5 h-5 shrink-0" />
                            <p className="text-sm font-bold tracking-tight">{error}</p>
                        </div>
                    )}

                    {step === "select" && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">Select Character</h3>
                                <div className="h-[1px] flex-grow bg-white/5 mx-4" />
                            </div>

                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-12 gap-4">
                                    <div className="relative">
                                        <div className="w-12 h-12 border-4 border-purple-500/20 rounded-full" />
                                        <div className="absolute inset-0 w-12 h-12 border-4 border-t-purple-500 rounded-full animate-spin" />
                                    </div>
                                    <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Searching Server...</p>
                                </div>
                            ) : characters.length === 0 ? (
                                <div className="text-center py-12 px-6 rounded-3xl bg-white/5 border border-dashed border-white/10">
                                    <User className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                                    <h4 className="text-slate-200 font-bold mb-1">No Characters Found</h4>
                                    <p className="text-slate-500 text-xs leading-relaxed max-w-[200px] mx-auto">
                                        We couldn't find any characters on your account for this server.
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-3 max-h-[280px] overflow-y-auto pr-2">
                                    {characters.map((char) => (
                                        <button
                                            key={char.charId}
                                            onClick={() => setSelectedChar(char.char_name)}
                                            className={`group relative flex items-center justify-between p-4 rounded-2xl border transition-all duration-200 ${selectedChar === char.char_name
                                                ? "bg-purple-600/20 border-purple-500/50 shadow-[0_0_20px_-5px_rgba(168,85,247,0.3)]"
                                                : "bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/20"
                                                }`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${selectedChar === char.char_name ? "bg-purple-500" : "bg-slate-800"
                                                    }`}>
                                                    <User className={`w-5 h-5 ${selectedChar === char.char_name ? "text-white" : "text-slate-400"}`} />
                                                </div>
                                                <div className="text-left">
                                                    <div className="text-white font-black text-sm uppercase tracking-tight group-hover:text-purple-300 transition-colors">
                                                        {char.char_name}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Level {char.level}</span>
                                                        <span className="w-1 h-1 rounded-full bg-slate-700" />
                                                        <span className={`text-[10px] font-bold uppercase tracking-widest ${char.online ? 'text-green-500' : 'text-slate-600'}`}>
                                                            {char.online ? 'Online' : 'Offline'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${selectedChar === char.char_name ? "border-purple-500 bg-purple-500" : "border-white/10"
                                                }`}>
                                                {selectedChar === char.char_name && <CheckCircle2 className="w-3 h-3 text-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="flex items-start gap-3 p-4 bg-purple-500/10 border border-purple-500/20 rounded-2xl">
                                <Sparkles className="w-5 h-5 text-purple-400 shrink-0" />
                                <p className="text-[11px] text-purple-200/80 font-medium leading-relaxed uppercase tracking-tight">
                                    Delivery is <span className="text-white font-bold tracking-widest">Instant</span> to your in-game mailbox.
                                    Open your mail to claim your items.
                                </p>
                            </div>
                        </div>
                    )}

                    {step === "confirm" && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">Confirm Order</h3>
                                <div className="h-[1px] flex-grow bg-white/5 mx-4" />
                            </div>

                            <div className="bg-slate-950/50 rounded-3xl border border-white/5 overflow-hidden shadow-2xl">
                                {/* Pack Summary */}
                                <div className="p-6 border-b border-white/5 bg-gradient-to-r from-purple-500/5 to-transparent flex items-center gap-4">
                                    <div className="w-16 h-16 rounded-2xl bg-slate-900 border border-white/10 overflow-hidden flex items-center justify-center">
                                        {pack.imagem ? (
                                            <img src={pack.imagem} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <Package className="w-8 h-8 text-slate-700" />
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="text-white font-bold text-lg leading-tight">{pack.nome}</h4>
                                        <div className="flex items-center gap-2 mt-1">
                                            <div className="px-2 py-0.5 bg-purple-500/20 rounded-md border border-purple-500/30">
                                                <span className="text-purple-400 text-[9px] font-black uppercase tracking-widest">Premium Pack</span>
                                            </div>
                                            <span className="text-slate-500 text-[10px] uppercase font-bold tracking-widest">×1 Unit</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Details List */}
                                <div className="p-6 space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Recipient</span>
                                        <div className="flex items-center gap-2">
                                            <Server className="w-3.5 h-3.5 text-blue-500" />
                                            <span className="text-sm font-black text-white uppercase tracking-tight">{selectedChar}</span>
                                        </div>
                                    </div>

                                    <div className="h-[1px] bg-white/5 w-full" />

                                    <div className="space-y-2">
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest block mb-3">Order Contents</span>
                                        {pack.items.map((item, idx) => (
                                            <div key={idx} className="flex justify-between items-center">
                                                <div className="flex items-center gap-2 text-slate-300">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500/50" />
                                                    <span className="text-xs font-medium">{item.nome} {item.enchant > 0 && `+${item.enchant}`}</span>
                                                </div>
                                                <span className="text-xs font-bold text-white tracking-widest">x{item.amount.toLocaleString()}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-6 p-4 bg-purple-500/10 rounded-2xl border border-purple-500/20 flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                            <Coins className="w-4 h-4 text-purple-400" />
                                            <span className="text-xs font-black text-purple-300 uppercase tracking-widest underline decoration-purple-500/30">Total Price</span>
                                        </div>
                                        <span className="text-xl font-black text-white tracking-tighter italic">
                                            {pack.valor.toLocaleString()} <span className="text-[10px] text-purple-500 not-italic uppercase tracking-[0.2em] ml-1">Credits</span>
                                        </span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    )}

                    {step === "processing" && (
                        <div className="py-20 flex flex-col items-center justify-center text-center space-y-6">
                            <div className="relative">
                                <div className="absolute inset-0 bg-purple-500 blur-3xl opacity-20 animate-pulse" />
                                <div className="relative w-24 h-24 rounded-full border-2 border-white/5 flex items-center justify-center">
                                    <div className="absolute inset-0 w-24 h-24 border-t-2 border-purple-500 rounded-full animate-spin" />
                                    <ShoppingBag className="w-10 h-10 text-white animate-bounce" />
                                </div>
                            </div>
                            <div>
                                <h3 className="text-2xl font-black text-white uppercase italic tracking-tight">Securing Order...</h3>
                                <p className="text-slate-400 font-bold text-xs uppercase tracking-[0.2em] mt-2">Connecting to game database</p>
                            </div>
                        </div>
                    )}

                    {step === "success" && (
                        <div className="py-12 flex flex-col items-center justify-center text-center space-y-8 animate-in zoom-in-95 duration-500">
                            <div className="relative">
                                <div className="absolute inset-0 bg-green-500 blur-[80px] opacity-30" />
                                <div className="relative w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-700 rounded-3xl flex items-center justify-center shadow-[0_20px_40px_-15px_rgba(34,197,94,0.5)] rotate-12">
                                    <CheckCircle2 className="w-12 h-12 text-white" />
                                </div>
                            </div>
                            <div>
                                <h3 className="text-3xl font-black text-white uppercase italic tracking-tighter">SUCCESS!</h3>
                                <p className="text-slate-400 font-bold text-xs uppercase tracking-[0.2em] mt-4 flex items-center justify-center gap-2">
                                    Deliverd to <span className="text-white underline decoration-green-500">{selectedChar}</span>
                                </p>
                                <div className="mt-8 p-4 bg-white/5 rounded-2xl border border-white/5 inline-block">
                                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest max-w-[280px]">
                                        Your items have been added to the private warehouse. Please log in to your account to claim them.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="relative z-10 p-8 pt-0 flex flex-col gap-3">
                    {step === "select" && (
                        <button
                            onClick={() => setStep("confirm")}
                            disabled={!selectedChar || loading}
                            className={`w-full py-5 rounded-2xl font-black text-sm uppercase italic tracking-widest flex items-center justify-center gap-3 transition-all duration-300 shadow-2xl ${!selectedChar || loading
                                ? "bg-slate-800 text-slate-600 border border-white/5 cursor-not-allowed"
                                : "bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:scale-[1.02] active:scale-[0.98] shadow-purple-500/20 border-t border-white/20"
                                }`}
                        >
                            Next Phase
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    )}
                    {step === "confirm" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                onClick={() => setStep("select")}
                                className="order-2 sm:order-1 w-full py-5 bg-white/5 hover:bg-white/10 text-slate-400 font-bold text-xs uppercase tracking-widest rounded-2xl border border-white/5 transition-all"
                            >
                                Re-config
                            </button>
                            <button
                                onClick={handlePurchase}
                                className="order-1 sm:order-2 w-full py-5 rounded-2xl font-black text-sm uppercase italic tracking-widest flex items-center justify-center gap-3 transition-all duration-300 shadow-2xl bg-white text-slate-950 hover:bg-purple-100 hover:scale-[1.02] active:scale-[0.98] shadow-white/10"
                            >
                                <Zap className="w-4 h-4 fill-current" />
                                Finalize Order
                            </button>
                        </div>
                    )}
                    {step === "success" && (
                        <button
                            onClick={onClose}
                            className="w-full py-5 bg-white text-slate-950 hover:bg-purple-100 font-black text-sm uppercase italic tracking-widest rounded-2xl shadow-2xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                        >
                            Return to Shop
                        </button>
                    )}
                </div>

                {/* Footer Decoration */}
                <div className="h-2 bg-gradient-to-r from-purple-600 via-blue-500 to-purple-600 animate-gradient-x" />
            </div>
        </div>
    )
}
